import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Bell, Check, Inbox, ArrowRight, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationIcon = ({ kind }) => {
  const map = {
    message: 'bg-sky-100 text-sky-600',
    like: 'bg-rose-100 text-rose-500',
    comment: 'bg-emerald-100 text-emerald-600',
    event: 'bg-amber-100 text-amber-600',
    member: 'bg-indigo-100 text-indigo-600',
    default: 'bg-slate-100 text-slate-500',
  };
  const cls = map[kind] || map.default;
  return (
    <div className={`w-10 h-10 rounded-full ${cls} flex items-center justify-center flex-shrink-0`}>
      <Bell className="w-4 h-4" />
    </div>
  );
};

const safeAgo = (iso) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ''; }
};

const AppNotificationsPage = () => {
  const [items, setItems] = useState(null);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications?limit=50`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { setItems([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    setMarking(true);
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true }).catch(() => null);
      setItems((prev) => (prev || []).map((n) => ({ ...n, read: true })));
    } finally { setMarking(false); }
  };

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-[#002249] animate-spin" />
      </div>
    );
  }

  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="px-4 pt-4 pb-2" data-testid="app-notifications">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#E63946]">Activity</p>
          <h1 className="text-[20px] font-bold text-[#002249] tracking-tight">Notifications</h1>
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            disabled={marking}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-slate-100 hover:bg-slate-200 text-[#002249] text-[12px] font-semibold disabled:opacity-50"
            data-testid="mark-all-read"
          >
            <Check className="w-3.5 h-3.5" /> Mark all read
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 px-6 bg-white rounded-2xl border border-slate-200" data-testid="notifications-empty">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <Inbox className="w-6 h-6 text-slate-400" />
          </div>
          <p className="text-[14px] font-bold text-[#002249]">All caught up</p>
          <p className="text-[12.5px] text-slate-500 mt-1">Notifications about likes, comments, messages and events will appear here.</p>
        </div>
      ) : (
        <ul className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {items.map((n, i) => (
            <motion.li
              key={n.id || i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.1) }}
              className={`relative ${!n.read ? 'bg-[#F8FBFF]' : ''}`}
            >
              <Link
                to={n.link || n.url || '/app/feed'}
                className="flex items-start gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors"
                data-testid={`notification-${i}`}
              >
                <NotificationIcon kind={n.kind || n.type} />
                <div className="min-w-0 flex-1">
                  {n.title && <p className="text-[13.5px] font-semibold text-[#002249] leading-tight">{n.title}</p>}
                  {n.body && <p className="text-[12.5px] text-slate-600 mt-0.5 line-clamp-2 leading-relaxed">{n.body}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">{safeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#E63946] mt-1.5 flex-shrink-0" />}
                <ArrowRight className="w-4 h-4 text-slate-300 mt-1 flex-shrink-0" />
              </Link>
            </motion.li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AppNotificationsPage;
